import {
  Badge,
  Button,
  Caption1,
  Card,
  Field,
  FluentProvider,
  Input,
  Label,
  ProgressBar,
  Slider,
  Switch,
  Tab,
  TabList,
  Text,
  Textarea,
  Title2,
  Tooltip,
  webLightTheme,
} from "@fluentui/react-components";
import {
  ArrowDownloadRegular,
  CheckmarkCircleRegular,
  KeyRegular,
  OpenRegular,
  PlayRegular,
  SaveRegular,
  SettingsRegular,
  SparkleRegular,
} from "@fluentui/react-icons";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { completeTask } from "./openAiClient";
import { getOfficeContext, insertIntoSelection, waitForOffice } from "./officeHost";
import { taskPresets } from "./prompts";
import { defaultSettings, loadSettings, saveSettings } from "./settings";
import type { AssistantSettings, OfficeContext } from "./types";
import "./styles.css";

type StatusKind = "idle" | "working" | "success" | "error";

type Status = {
  kind: StatusKind;
  message: string;
};

export default function App() {
  const [settings, setSettings] = useState<AssistantSettings>(defaultSettings);
  const [host, setHost] = useState("Loading");
  const [officeReady, setOfficeReady] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [result, setResult] = useState("");
  const [context, setContext] = useState<OfficeContext>({ host: "Browser", selection: "" });
  const [status, setStatus] = useState<Status>({ kind: "idle", message: "Ready" });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [autoInsert, setAutoInsert] = useState(false);
  const [activeTab, setActiveTab] = useState<"compose" | "settings">("compose");

  const statusTone = useMemo(() => {
    if (status.kind === "success") return "success";
    if (status.kind === "error") return "danger";
    if (status.kind === "working") return "brand";
    return "subtle";
  }, [status.kind]);

  const refreshContext = useCallback(async (nextHost = host) => {
    const nextContext = await getOfficeContext(nextHost);
    setContext(nextContext);
  }, [host]);

  useEffect(() => {
    setSettings(loadSettings());
    void waitForOffice().then((state) => {
      setHost(state.host);
      setOfficeReady(state.isOfficeReady);
      void refreshContext(state.host);
    });
  }, [refreshContext]);

  function updateSettings(next: Partial<AssistantSettings>) {
    setSettings((current) => ({ ...current, ...next }));
  }

  function persistSettings() {
    const normalized = saveSettings(settings);
    setSettings(normalized);
    setStatus({ kind: "success", message: "Settings saved locally" });
  }

  async function runTask(event?: FormEvent<HTMLFormElement>) {
    event?.preventDefault();
    if (!instruction.trim()) {
      setStatus({ kind: "error", message: "Enter a task first" });
      return;
    }

    setStatus({ kind: "working", message: "Reading Office context" });
    const currentContext = await getOfficeContext(host);
    setContext(currentContext);

    try {
      setStatus({ kind: "working", message: "Generating completion" });
      const completion = await completeTask({
        instruction,
        context: currentContext,
        settings,
      });
      setResult(completion);

      if (autoInsert && officeReady) {
        await insertIntoSelection(completion);
        setStatus({ kind: "success", message: "Generated and inserted" });
      } else {
        setStatus({ kind: "success", message: "Completion ready" });
      }
    } catch (error) {
      setStatus({
        kind: "error",
        message: error instanceof Error ? error.message : "Task failed",
      });
    }
  }

  async function insertResult() {
    if (!result.trim()) {
      setStatus({ kind: "error", message: "Generate a completion first" });
      return;
    }

    try {
      await insertIntoSelection(result);
      setStatus({ kind: "success", message: "Inserted into selection" });
    } catch (error) {
      setStatus({
        kind: "error",
        message: error instanceof Error ? error.message : "Insert failed",
      });
    }
  }

  return (
    <FluentProvider theme={webLightTheme}>
      <main className="appShell">
        <section className="heroBand">
          <div className="brandMark" aria-hidden="true">
            <SparkleRegular />
          </div>
          <div>
            <Text className="eyebrow">VoltBox Assistant</Text>
            <Title2 as="h1">Office task completion</Title2>
          </div>
          <Badge appearance="tint" color={officeReady ? "success" : "warning"}>
            {officeReady ? host : "Preview"}
          </Badge>
        </section>

        <TabList
          selectedValue={activeTab}
          onTabSelect={(_, data) => setActiveTab(data.value as "compose" | "settings")}
          size="small"
        >
          <Tab icon={<SparkleRegular />} value="compose">
            Compose
          </Tab>
          <Tab icon={<SettingsRegular />} value="settings">
            Settings
          </Tab>
        </TabList>

        {activeTab === "compose" ? (
          <form className="stack" onSubmit={runTask}>
            <section className="contextBand">
              <div>
                <Label>Current selection</Label>
                <Caption1>
                  {context.selection
                    ? `${context.selection.length.toLocaleString()} characters available`
                    : "No selection text detected"}
                </Caption1>
              </div>
              <Tooltip content="Refresh selection" relationship="label">
                <Button appearance="subtle" icon={<OpenRegular />} onClick={() => refreshContext()} />
              </Tooltip>
            </section>

            <Field label="Task">
              <Textarea
                className="taskInput"
                resize="vertical"
                value={instruction}
                onChange={(_, data) => setInstruction(data.value)}
                placeholder="Ask VoltBox Assistant to rewrite, summarize, analyze, draft, or extract from the current Office selection."
              />
            </Field>

            <div className="presetGrid" aria-label="Task presets">
              {taskPresets.map((preset) => (
                <Button key={preset} appearance="secondary" onClick={() => setInstruction(preset)}>
                  {preset}
                </Button>
              ))}
            </div>

            <div className="actionRow">
              <Switch
                checked={autoInsert}
                onChange={(_, data) => setAutoInsert(data.checked)}
                label="Insert automatically"
              />
              <Button appearance="primary" icon={<PlayRegular />} type="submit">
                Run
              </Button>
            </div>

            {status.kind === "working" ? <ProgressBar thickness="medium" /> : null}

            <Card className="resultPanel" appearance="filled-alternative">
              <div className="resultHeader">
                <div>
                  <Label>Completion</Label>
                  <Caption1>Review before inserting into the active Office file.</Caption1>
                </div>
                <Tooltip content="Insert completion" relationship="label">
                  <Button icon={<ArrowDownloadRegular />} onClick={insertResult} disabled={!result} />
                </Tooltip>
              </div>
              <Textarea
                className="resultText"
                resize="vertical"
                value={result}
                onChange={(_, data) => setResult(data.value)}
                placeholder="Generated content appears here."
              />
            </Card>
          </form>
        ) : (
          <section className="stack">
            <Card className="settingsPanel" appearance="filled-alternative">
              <div className="settingsHeader">
                <div>
                  <Label>OpenAI-compatible API</Label>
                  <Caption1>Settings are stored in this browser profile.</Caption1>
                </div>
                <Button
                  icon={settingsOpen ? <CheckmarkCircleRegular /> : <KeyRegular />}
                  onClick={() => setSettingsOpen((value) => !value)}
                >
                  {settingsOpen ? "Configured" : "Configure"}
                </Button>
              </div>

              {settingsOpen ? (
                <div className="settingsGrid">
                  <Field label="API base URL">
                    <Input
                      value={settings.apiBaseUrl}
                      onChange={(_, data) => updateSettings({ apiBaseUrl: data.value })}
                      placeholder="https://api.openai.com/v1"
                    />
                  </Field>
                  <Field label="API key (optional)" hint="Leave blank for providers that do not require authentication.">
                    <Input
                      type="password"
                      value={settings.apiKey}
                      onChange={(_, data) => updateSettings({ apiKey: data.value })}
                      placeholder="sk-..."
                    />
                  </Field>
                  <Field label="Model">
                    <Input
                      value={settings.model}
                      onChange={(_, data) => updateSettings({ model: data.value })}
                      placeholder="gpt-4.1-mini"
                    />
                  </Field>
                  <Field
                    label={`Temperature ${settings.temperature.toFixed(1)}`}
                    hint="Lower values keep Office edits steadier."
                  >
                    <Slider
                      min={0}
                      max={2}
                      step={0.1}
                      value={settings.temperature}
                      onChange={(_, data) => updateSettings({ temperature: data.value })}
                    />
                  </Field>
                  <Field label="System prompt">
                    <Textarea
                      value={settings.systemPrompt}
                      resize="vertical"
                      onChange={(_, data) => updateSettings({ systemPrompt: data.value })}
                    />
                  </Field>
                  <Button appearance="primary" icon={<SaveRegular />} onClick={persistSettings}>
                    Save settings
                  </Button>
                </div>
              ) : null}
            </Card>
          </section>
        )}

        <footer className={`statusLine status-${statusTone}`}>
          <span />
          <Caption1>{status.message}</Caption1>
        </footer>
      </main>
    </FluentProvider>
  );
}
